<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useRoute } from 'vue-router';

import type {
  INoneNegativeCarryoverFormComposableOptions,
} from '@modules/affiliates-approved/submodules/none-negative-carryover/composables/useNoneNegativeCarryoverForm';
import type { INoneNegativeCarryoverForm } from '@modules/affiliates-approved/submodules/none-negative-carryover/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VLoader from '@components/loader/VLoader.vue';
import { useNoneNegativeCarryoverForm } from '@modules/affiliates-approved/submodules/none-negative-carryover/composables';
import {
  ADMIN_AFFILIATE_NONE_NEGATIVE_CARRYOVER_ROUTE_NAME,
  AFFILIATES_NONE_NEGATIVE_CARRYOVER_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER,
} from '@modules/affiliates-approved/submodules/none-negative-carryover/constants';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { useMerchantIdStore } from '@modules/merchant/store';
import { isString } from '@utils/guards';

import type { IAffiliateNoneNegativeCarryoverHistoryEditPageProps } from './types';
import { TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER_HISTORY_EDIT } from '../../constants';
import { useEditHistoryNoneNegativeCarryoverMutation } from '../../mutations';
import { useShowHistoryNoneNegativeCarryoverQuery } from '../../queries';

const props = defineProps<IAffiliateNoneNegativeCarryoverHistoryEditPageProps>();

const backToRoute = { name: ADMIN_AFFILIATE_NONE_NEGATIVE_CARRYOVER_ROUTE_NAME };
const route = useRoute();
const { formatMonthDate } = useFormatMonthDate();
const merchantId = toRef(useMerchantIdStore(), 'merchantId');

const affiliateId = computed(() => (isString(route.params.id) ? Number(route.params.id) : 0));
const itemId = computed(() => (isString(route.params.itemId) ? Number(route.params.itemId) : 0));

const { data, isLoading } = useShowHistoryNoneNegativeCarryoverQuery(itemId);

const {
  mutateAsync,
  isLoading: isEditLoading,
  errors,
  clearError,
} = useEditHistoryNoneNegativeCarryoverMutation();

const formOptions = computed<INoneNegativeCarryoverFormComposableOptions>(() => ({
  enableNncoValue: data.value?.enable_nnco,
  dateValue: data.value?.date ? new Date(data.value.date) : undefined,
  merchantNameValue: props.merchantName,
}));

const { formSchema, formGroups } = useNoneNegativeCarryoverForm(formOptions);

async function onSubmit({ enable_nnco, date }: INoneNegativeCarryoverForm): Promise<void> {
  await mutateAsync({
    itemId: itemId.value,
    merchant: merchantId.value,
    history_of_nncos_change: {
      affiliate_id: affiliateId.value,
      enable_nnco,
      date: formatMonthDate(date),
    },
  });
}
</script>

<template>
  <VLoader v-data-qa-auto-id="'AffiliateNoneNegativeCarryoverHistoryEditPage'"
    :loading="isLoading"
    min-height="340px"
  >
    <div class="p-5">
      <h4 class="p-card-title mb-3 text-lg">
        {{ $getTranslation(TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER_HISTORY_EDIT, 'HEADER') }}
      </h4>
      <VForm
        :schema="formSchema"
        :groups="formGroups"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER, 'SUBMIT')"
        :custom-errors="errors"
        :is-loading="isEditLoading"
        :readonly="!$hasPermission(AFFILIATES_NONE_NEGATIVE_CARRYOVER_PERMISSIONS.EDIT)"
        @submit="onSubmit"
        @interacted="clearError"
      >
        <template #secondaryBtn>
          <VButton
            :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER_HISTORY_EDIT, 'CLOSE')"
            :to="backToRoute"
            outlined
          />
        </template>
      </VForm>
    </div>
  </VLoader>
</template>
